import { AutocompleteFilterTypes, FilterType } from '@/services/searchEngine/enums/FilterType';
import { BaseFilter, LRange } from '@/models/Filter';
import DictionaryStore from '@/commons/services/store/DictionaryStore';
import ClientStore from '@/modules/client/services/store/ClientStore';
import { banksDictionary } from '@/commons/dictionaries/BanksDictionary';
import { every } from 'lodash-es';
import { findEnumByKeys } from '@/services/enumTranslator/FindEnumByKeys';
import { normalizeNumberEnum } from '@/services/enumTranslator/normalizeNumberEnum';
import UserStore from '@/modules/user/services/store/UserStore';
import { DictionaryStoreState } from '@/commons/services/store/DictionaryStoreState';
import DateUtils from '@/services/utils/DateUtils';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { isDeepEmpty } from '@/services/utils/BasicUtils';
import { Address } from '@/modules/multiForm/shared/MultiformModels';
import useGeoAreas from '@/composables/useGeoAreas';

export interface IDataTableHeader<T, TFilter extends BaseFilter = BaseFilter> {
  text: string
  filterType?: FilterType | Record<keyof any, any>;
  i18nEnumName?: string;
  valueFilter?: keyof TFilter
  valueView?: (value: T) => string | Promise<string>
  valueSort?: keyof T | string;
  suffix?: string;
  condition?: boolean;
  filterOrder?: number;
  activeFilterLabel?: string;
  id?: string;
  filterReadonly?: boolean;
  endOfDayFormat?: boolean;
  width?: string;
  align?: string;
  dictName?: keyof DictionaryStoreState;
  defaultValue?: unknown;
  defaultValueLabel?: string;
  nonClearAbleProperties?: Array<string>
  filterCondition?: boolean | (() => boolean);
  headerPrefix?: string;

  valueText?(value: any, filter?: TFilter): Promise<string>;
}

export class DataTableHeader<T = any, TFilter extends BaseFilter = BaseFilter> implements IDataTableHeader<T, TFilter> {
  text!: string;
  value!: keyof T | string;
  valueView?: ((a: T) => string);
  valueFilter?: keyof TFilter;
  sortable: boolean = false;
  filterType?: FilterType | Record<keyof any, any>;
  condition: boolean = true;
  suffix?: string;
  filterOrder?: number;
  activeFilterLabel?: string;
  existingItemsKey?: object[] = [];
  width?: string;
  align?: string;
  dictName?: keyof DictionaryStoreState;
  nonClearAbleProperties?: Array<string>;

  async valueText(value:
    (string
      | string[]
      | number[]
      | LRange<number>
      | Array<LRange<any>> |
      Address),): Promise<string> {
    const isValueExist: boolean =
      value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
        ? !isDeepEmpty(value)
        : Array.isArray(value)
          ? this.checkArrayValues(value)
          : Boolean(value);
    if (isValueExist) {
      switch (this.filterType) {
        case FilterType.TEXT:
          return value as string;
        case FilterType.DATE:
          const valueDate: Array<LRange<Date>> = (value as unknown as Array<LRange<Date>>);
          return valueDate
            .filter(el => (el.to !== null || el.from !== null))
            .map(el => `${this.text}: ${el.from ? el.from.toISOString().slice(0, 10) : '...'} - ${el.to ? el.to.toISOString().slice(0, 10) : '...'} `).join(', ');
        case FilterType.RANGE:
          const valueNumber: LRange<number> = (value as LRange<number>);
          return `${valueNumber.from || 0} - ${valueNumber.to || ''}`;
        case FilterType.NUMBER:
          return value as string;
        case FilterType.AUTOCOMPLETE:
          const valueAutocomplete: number[] = Array.isArray(value) ? value as number[] : [value,] as unknown as number[]
          switch (this.valueFilter) {
            case AutocompleteFilterTypes.CLIENTS:
              return ClientStore.getClients.filter(client => valueAutocomplete.includes(client.id)).map(client => client.displayName).join(', ');
            case AutocompleteFilterTypes.USERS:
              return UserStore.getUsersForFilters.filter(user => valueAutocomplete.includes(user.id)).map(user => user.displayName).join(', ');
            case AutocompleteFilterTypes.OWNER_ID:
              return UserStore.getUsersForFilters.filter(user => valueAutocomplete.includes(user.id)).map(user => user.displayName).join(', ');
            case AutocompleteFilterTypes.PARTNERS:
              const partnerDictionary = await DictionaryStore.getDictionary('partnerDictionary');
              return partnerDictionary.filter(partner => valueAutocomplete.includes(partner.id)).map(partner => partner.displayName).join(', ');
            case AutocompleteFilterTypes.DIVISIONS:
              const divisionsDictionary = await DictionaryStore.getDictionary('divisionsDictionary');
              return divisionsDictionary.flatMap(division => division.divisions).filter(divisionMember => valueAutocomplete.includes(divisionMember.id)).map(divisionMember => divisionMember.name).join(', ');
            case AutocompleteFilterTypes.BANKS:
              return banksDictionary().filter(bank => bank.id && valueAutocomplete.includes(bank.id)).map(bank => bank.name_pl).join(', ');
          }
          break;
        case FilterType.RADIO:
          return value ? (this.text + ': ' + I18NGetter().useButtonCommons.YES) : (this.text + ': ' + I18NGetter().useButtonCommons.NO);
        case FilterType.STORE_DICT:
          if (this.dictName) {
            const dictValue: number[] = value as number[];
            const dictionary = await DictionaryStore.getDictionary(this.dictName);
            return (dictionary as Array<any>).filter((item: any) => dictValue.includes(item.id)).map((item: any) => item.reason).join(', ');
          } else {
            return this.text;
          }
        case FilterType.ADDRESS:
          if (isDeepEmpty(value as Address)) {
            return this.text;
          } else {
            return Object.entries(value).reduce<string[]>((acc, [key, value,]) => {
              if (!value) return acc
              if (this.nonClearAbleProperties?.includes(key)) {
                return acc
              }
              if (key === 'districtId') {
                const districtName: string = useGeoAreas().districtDictionary().find(district => district.type === value)?.name_pl || '';
                acc.push(districtName)
              }
              if (key === 'landId') {
                const landName: string = useGeoAreas().landDictionary().find(land => land.type === value)?.name_pl || '';
                acc.push(landName)
              } else if (value) acc.push(value);
              return acc
            }, []).join(', ');
          }
        default:
          return !Array.isArray(value)
            ? findEnumByKeys((<any>this.filterType), value as string)
            : String((<string[]>value)
              .map((element: string) => findEnumByKeys((<any>this.filterType), element)
              )
              .join(', '));
      }
    }
    return this.text;
  }

  private checkArrayValues(value:
    Array<string>
    | Array<number>
    | Array<LRange<string>>
    | Array<Record<keyof TFilter, LRange<Date>>>): boolean {
    return value.length > 0 &&
      every(value, (el:
        string
        | number
        | LRange<string>
        | Record<keyof TFilter, LRange<Date>>) => {
        if (el instanceof LRange) {
          return (el.to !== null || el.from !== null);
        } else if (el && typeof el === 'object') {
          return Object.values(el).some(el => (el.to !== null || el.from !== null));
        } else {
          return el !== undefined && el !== null;
        }
      });
  }

  constructor(dto: IDataTableHeader<T, TFilter>) {
    if (dto.valueSort) {
      this.sortable = true;
      this.value = dto.valueSort;
    }
    if (dto.filterType && typeof dto.filterType === 'object') {
      dto.filterType = normalizeNumberEnum(dto.filterType);
    }
    Object.assign(this, dto);
  }
}

export class FilterGroup implements IDataTableHeader<any> {
  public filterType!: FilterType;
  public text!: string;
  public headers!: IDataTableHeader<any>[];
  public filterOrder?: number;
  public activeFilterLabel?: string;
  public range?: LRange<number>;

  constructor(filterType: FilterType, headers: IDataTableHeader<any>[], text: string, filterOrder?: number) {
    this.filterType = filterType;
    this.headers = headers;
    this.text = text;
    if (filterOrder && filterOrder >= 0) {
      this.filterOrder = filterOrder;
    }
  }

  async valueText(value: Array<Record<keyof BaseFilter, boolean | LRange<Date>>>): Promise<string> {
    const types: Array<string> = value.flatMap(el => Object.keys(el));
    const texts: Array<string> = [];

    this.headers.map(header => {
      if (!header.valueFilter || !types.includes(header.valueFilter)) {
        return;
      }

      if (header.filterType === FilterType.DATE) {
        const headerValue: LRange<Date> =
          (value.find(item => !!item[header.valueFilter!])!)[header.valueFilter!] as LRange<Date>;
        const fromDate = headerValue.from;
        const toDate = headerValue.to;
        let text = '';
        const defaultValue: LRange<Date> | undefined = header.defaultValue as LRange<Date>;
        if (header.defaultValueLabel && header.defaultValue &&
          ((!defaultValue.from && !fromDate) || `${defaultValue.from}` === `${fromDate}`) &&
          ((!defaultValue.to && !toDate) || `${defaultValue.to}` === `${toDate}`)) {
          text = header.defaultValueLabel.toLocaleLowerCase();
        } else if (fromDate && toDate) {
          text = `${DateUtils.getFormatedDate(fromDate!)}-${DateUtils.getFormatedDate(toDate!)}`;
        } else if (fromDate) {
          text = `${I18NGetter().useTimeCommons.FROM} ${DateUtils.getFormatedDate(fromDate!)}`;
        } else if (toDate) {
          text = `${I18NGetter().useTimeCommons.TO} ${DateUtils.getFormatedDate(toDate!)}`;
        }
        texts.push(`${header.text}: ${text}`);
      } else if (header.filterType === FilterType.RADIO || header.filterType === FilterType.RADIO_EXPERT_ATTRIBUTES) {
        value.forEach(item => {
          if (item[header.valueFilter!] !== null && item[header.valueFilter!] !== undefined) {
            texts.push(header.text + ': ' + (item[header.valueFilter!] ? I18NGetter().useButtonCommons.YES : I18NGetter().useButtonCommons.NO));
          }
        })
      } else {
        texts.push(header.text);
      }
    });
    return (texts.length && texts.join(', ')) || this.text;
  }
}
